import { useRef, useState } from 'react';

import { useEventListener } from './useEventListener';
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

interface WindowSize {
  width: number;
  height: number;
}

interface WindowSizeState {
  previous: WindowSize | null;
  current: WindowSize;
}

const useWindowSize = () => {
  const [isReady, setIsReady] = useState(false);
  const history = useRef<[WindowSize | null, WindowSize] | null>(null);
  const [states, setStates] = useState<WindowSizeState>({
    previous: null,
    current: {
      width: 0,
      height: 0,
    },
  });

  const handleSize = () => {
    const updateWindowSize: WindowSize = {
      width: window.innerWidth,
      height: Math.max(window.innerHeight, document.documentElement.clientHeight),
    };

    if (!history.current) {
      history.current = [null, updateWindowSize];
    } else {
      history.current = [history.current[1], updateWindowSize];
    }

    setStates({
      previous: history.current[0],
      current: history.current[1],
    });

    setIsReady(true);
  };

  useEventListener(window, 'resize', handleSize);

  // Set size at the first client-side load
  useIsomorphicLayoutEffect(() => {
    handleSize();
    return () => {
      setIsReady(false);
    };
  }, []);

  return {
    ...states,
    isReady,
  };
};

export { useWindowSize };
